import { useEffect, useRef, useState, type ReactNode } from "react";

/**
 * Wraps a row of board controls (search bar, toggles, expand/collapse) and
 * re-pins it as a fixed bar at the top of the viewport once the inline row
 * scrolls up behind the sticky app header. The children are rendered twice:
 * once inline (the real, in-flow controls) and once in the floating clone.
 */
export function FloatingControlsBar({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [isFloating, setIsFloating] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Hand off to the floating bar as soon as the inline controls pass behind
    // the sticky app header, so there's no gap where the controls are hidden
    // under it. Measure the header's actual rendered height rather than
    // hard-coding --app-header-height in px, so the two can't drift apart if the
    // root font size (and thus the rem-based header height) changes.
    const headerHeight =
      document.querySelector(".app-header")?.getBoundingClientRect().height ?? 80;
    const observer = new IntersectionObserver(
      ([entry]) => setIsFloating(!entry!.isIntersecting),
      { threshold: 0, rootMargin: `-${headerHeight}px 0px 0px 0px` },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <div ref={ref} className="board-controls">
        {children}
      </div>
      {isFloating && (
        <div className="board-controls board-controls--floating">{children}</div>
      )}
    </>
  );
}
